import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import I18n from 'I18n';
import { ACTION_IDS } from 'asset-actions-lib/constants/actionIds';
import { FORM_ACTION_IDS } from 'asset-actions-lib-forms/constants/actionIds';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import FormTypes from 'forms-utils-lib/constants/formDefinition/FormTypes';
import FrameworkTableContext from 'FormsUI/components/dashboard/frameworkTable/FrameworkTableContext';
import { trackDashboardInteraction } from 'FormsUI/utils/TrackingUtils';
import { getV2EditorRoute, getPopupEditorRoute, getV4EditorRoute } from 'FormsUI/lib/RouteGenerators';
import { navigateTo } from 'FormsUI/utils/UrlUtils';
import { getFormEditorWithParams } from 'FormsUI/lib/RouteGenerators';
import { getQueryParams } from 'FormsUI/utils/QueryParams';
import { VIEW_SIDE_PANEL, TEST_FORM, VIEW_MOVE_TO_FOLDER, VIEW_SUBMISSIONS, VIEW_EXPORT_FORM_SUBMISSIONS, VIEW_FILE_UPLOADS, CREATE_CONTACTS_LIST, VIEW_TRANSLATE_FORM, VIEW_CAMPAIGN, VIEW_ASSIGN_PERMISSIONS, DELETE_FORM, VIEW_DELETE_FORM, VIEW_CLONE_DIALOG, VIEW_RENAME, VIEW_COLLECTED_FORM_DETAILS, VIEW_EDIT_MAILCHIMP_LISTS, EDIT_EMAIL_NOTIFICATIONS, OPEN_CONVERT_FOLLECTED_FORM_DIALOG, UNPUBLISH_FORM } from 'FormsUI/constants/TrackingInteractionKeys/Dashboard/Actions';
// @ts-expect-error no types
import { FormErrors, parseFormError } from 'FormsUI/api/Forms';
// @ts-expect-error no types
import { failCloningForm } from 'FormsUI/actions/formCloning/cloneForm';
import { showExportSuccessMessage, showExportFailureMessage } from 'FormsUI/actions/SubmissionExport';
import { updateParams } from 'FormsUI/actions/ReactRouter';
import { publishFormDefinitionError, validateFormError
// @ts-expect-error no types
} from 'FormsUI/actions/FormSaving';
import { trackInteraction, trackActivation, trackUsage, trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { PUBLISH_GO_TO_CTAS, PUBLISH_GO_TO_CMS } from 'FormsUI/constants/TrackingInteractionKeys/Editor/Global';
// @ts-expect-error Not migrated yet
import { succeededRenamingCollectedForm } from 'FormsUI/actions/CollectedForms';
import { updateCRMObjectName, updateCRMObjectPublishState } from 'FormsUI/components/dashboard/frameworkTable/utils';
import PublishState from 'FormsUI/constants/PublishState';
// @ts-expect-error no types
import { succeededUnpublishForm } from 'FormsUI/actions/LeadFlows';
export default function useActionsLib(formType) {
  const dispatch = useDispatch();
  const {
    setSidePanelId
  } = useContext(FrameworkTableContext);
  const trackAction = useCallback(action => trackDashboardInteraction(action, {
    via: 'action',
    formType
  }), [formType]);
  const handleActionError = ({
    actionId,
    error,
    data
  }) => {
    switch (actionId) {
      default:
        return null;
      case FORM_ACTION_IDS.CREATE_TRANSLATION:
        {
          if (!error) {
            return null;
          }
          if (error.message && error.message === 'Invalid form') {
            return dispatch(validateFormError(error.cause));
          }
          if (error.cause !== 'CLONE_ERROR' && error.cause !== 'PUBLISH_FORM_DEFINITION_ERROR') {
            return dispatch(validateFormError(error.cause));
          }
          if ('networkError' in error && error.networkError && 'xhr' in error.networkError && error.networkError.xhr) {
            if (error.cause === 'CLONE_ERROR') {
              switch (error.networkError.xhr.status) {
                case 0:
                  return dispatch(failCloningForm(FormErrors.NO_NETWORK));
                case 404:
                  return dispatch(failCloningForm(FormErrors.NOT_FOUND));
                case 409:
                  return dispatch(failCloningForm(FormErrors.DUPLICATE_NAME));
                default:
                  return dispatch(failCloningForm(parseFormError(error.networkError.xhr)));
              }
            }
            return dispatch(publishFormDefinitionError(error.networkError.xhr));
          }
          return null;
        }
      case FORM_ACTION_IDS.SELECT_CAMPAIGN:
        return dispatch({
          type: '@@campaigns/FAILED_ASSOCIATE_CAMPAIGN',
          meta: {
            notification: {
              type: 'danger',
              message: I18n.text('campaignAssociation.error.message')
            }
          }
        });
      case FORM_ACTION_IDS.EXPORT_SUBMISSIONS:
        return dispatch(showExportFailureMessage(data));
      case FORM_ACTION_IDS.CLONE:
        if (!error) {
          return null;
        }
        if ('networkError' in error && error.networkError && 'xhr' in error.networkError && error.networkError.xhr) {
          if (error.cause === 'CLONE_ERROR') {
            switch (error.networkError.xhr.status) {
              case 0:
                return dispatch(failCloningForm(FormErrors.NO_NETWORK));
              case 404:
                return dispatch(failCloningForm(FormErrors.NOT_FOUND));
              case 409:
                return dispatch(failCloningForm(FormErrors.DUPLICATE_NAME));
              default:
                return dispatch(failCloningForm(parseFormError(error.networkError.xhr)));
            }
          }
          return dispatch(publishFormDefinitionError(error.networkError.xhr));
        }
        return null;
    }
  };
  const handleActionSuccess = ({
    actionId,
    data
  }) => {
    switch (actionId) {
      default:
        return null;
      case FORM_ACTION_IDS.CLONE:
        {
          const clonedForm = data.clonedForm;
          const newFormId = clonedForm.id || clonedForm.guid || clonedForm.formGuid;

          // new editor form
          if ('id' in clonedForm) {
            return navigateTo(getV4EditorRoute(newFormId, 'cloned'));
          }
          switch (clonedForm.formType) {
            case FormTypes.FLOW:
              return navigateTo(getPopupEditorRoute(newFormId));
            default:
              return navigateTo(getV2EditorRoute(newFormId));
          }
        }
      case FORM_ACTION_IDS.VIEW_DETAILS:
        return setSidePanelId(data.objectId);
      case FORM_ACTION_IDS.VIEW_SUBMISSIONS:
        return trackAction(VIEW_SUBMISSIONS);
      case FORM_ACTION_IDS.CREATE_TRANSLATION:
        {
          const publishedForm = data.publishedForm;
          const params = getQueryParams();
          window.location.replace(getFormEditorWithParams(publishedForm.guid, Object.assign({}, params, {
            lang: data.language
          })));
          return null;
        }
      case ACTION_IDS.COMPARE:
        return trackAction('form-action-go-to-comparison-tool');
      case ACTION_IDS.DELETE:
        {
          if (data) {
            listingLibObserver.delete(data.objectId);
          }
          return trackAction(DELETE_FORM);
        }
      case FORM_ACTION_IDS.SHARE:
        {
          const maybeActionType = data ? data.actionType : '';
          switch (maybeActionType) {
            default:
              break;
            case 'copy-embed-code':
              {
                trackInteraction('embed-code-copy');
                trackActivation('embed-code-copy');
                trackUsage('embed-code-copy');
                break;
              }
            case 'click-share-link':
              trackInteraction('share-link-preview');
              break;
            case 'copy-share-link':
              trackInteraction('share-link-copy');
              trackActivation('share-link-copy');
              break;
            case 'click-cms-route':
              trackEditorInteraction(PUBLISH_GO_TO_CMS);
              break;
            case 'click-cta-route':
              trackEditorInteraction(PUBLISH_GO_TO_CTAS);
              break;
          }
          return null;
        }
      case FORM_ACTION_IDS.RENAME:
        {
          const {
            crmObject,
            newFormName
          } = data;
          listingLibObserver.update(updateCRMObjectName(crmObject, newFormName));
          return dispatch(succeededRenamingCollectedForm());
        }
      case FORM_ACTION_IDS.UNPUBLISH:
        {
          if (!data || !data.crmObject) {
            return null;
          }
          const {
            crmObject
          } = data;
          const updatedCrmObject = updateCRMObjectPublishState(crmObject, PublishState.DRAFT);
          listingLibObserver.update(updatedCrmObject);
          return dispatch(succeededUnpublishForm());
        }
      case FORM_ACTION_IDS.EXPORT_SUBMISSIONS:
        return dispatch(showExportSuccessMessage());
    }
  };
  const handleOnTrackEvent = (_, eventProps) => {
    if (eventProps.action !== 'click-action') {
      return null;
    }
    const {
      actionType,
      crmObject
    } = eventProps;
    switch (actionType) {
      default:
        return null;
      case FORM_ACTION_IDS.CLONE:
        return trackAction(VIEW_CLONE_DIALOG);
      case FORM_ACTION_IDS.VIEW:
        return trackAction(TEST_FORM);
      case FORM_ACTION_IDS.VIEW_DETAILS:
        return trackAction(VIEW_SIDE_PANEL);
      case FORM_ACTION_IDS.EXPORT_SUBMISSIONS:
        return trackAction(VIEW_EXPORT_FORM_SUBMISSIONS);
      case FORM_ACTION_IDS.VIEW_FILE_UPLOADS:
        return trackAction(VIEW_FILE_UPLOADS);
      case FORM_ACTION_IDS.CREATE_LIST:
        return trackAction(CREATE_CONTACTS_LIST);
      case FORM_ACTION_IDS.CREATE_TRANSLATION:
        return trackAction(VIEW_TRANSLATE_FORM);
      case FORM_ACTION_IDS.SELECT_CAMPAIGN:
        return trackAction(VIEW_CAMPAIGN);
      case ACTION_IDS.MANAGE_ACCESS:
        return trackAction(VIEW_ASSIGN_PERMISSIONS);
      case 'move-to-folder':
        return trackAction(VIEW_MOVE_TO_FOLDER);
      case ACTION_IDS.DELETE:
        return trackAction(VIEW_DELETE_FORM);
      case FORM_ACTION_IDS.RENAME:
        return trackAction(VIEW_RENAME);
      case 'view-non-hs-information':
        {
          if (crmObject) {
            const formGuid = getPropertyValue(crmObject, 'hs_form_id') || '';
            dispatch(updateParams({
              collectedFormsId: formGuid
            }));
            return trackAction(VIEW_COLLECTED_FORM_DETAILS);
          }
          break;
        }
      case FORM_ACTION_IDS.EDIT_NON_HS_MAILCHIMP_LIST:
        {
          if (crmObject) {
            const formGuid = getPropertyValue(crmObject, 'hs_form_id') || '';
            dispatch(updateParams({
              collectedFormsIdESP: formGuid
            }));
            return trackAction(VIEW_EDIT_MAILCHIMP_LISTS);
          }
          break;
        }
      case FORM_ACTION_IDS.EDIT_NON_HS_EMAIL_NOTIFICATIONS:
        return trackAction(EDIT_EMAIL_NOTIFICATIONS);
      case 'convert':
        return trackAction(OPEN_CONVERT_FOLLECTED_FORM_DIALOG);
      case FORM_ACTION_IDS.UNPUBLISH:
        return trackAction(UNPUBLISH_FORM);
    }
  };
  return {
    handleActionError,
    handleActionSuccess,
    handleOnTrackEvent
  };
}